import { rgba } from 'polished';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import { RecommendBackground } from '@treams/treams-icons';
import { FeatureName } from '@treams/treams-network';
import { tokens } from '@treams/treams-ui';

const STORAGE_KEY = 'hide-recommend';

const CardLink = styled(Link)`
	position: relative;
	display: block;
	width: 100%;
	align-self: flex-end;
	border-radius: 12px;
	padding: 16px 20px;
	background: ${(props) => rgba(props.theme.colors.primaryVibrant, 0.04)};
	border: 1px solid ${(props) => rgba(props.theme.colors.primary, 0.15)};
	text-decoration: none;
	overflow: hidden;
	transition: background 0.15s ease-out;

	&:hover {
		background: ${(props) => rgba(props.theme.colors.primaryVibrant, 0.08)};
	}
`;

const BackgroundDecoration = styled.img`
	position: absolute;
	top: 0;
	right: 0;
	pointer-events: none;
	user-select: none;
	width: 70px;
`;

const Content = styled.div`
	position: relative;
`;

const Eyebrow = styled.div`
	font-size: 12px;
	font-weight: 700;
	text-transform: uppercase;
	color: ${(props) => props.theme.colors.primary};
	margin-bottom: 6px;
`;

const Title = styled.div`
	font-size: 16px;
	font-weight: 700;
	color: ${tokens.text.primary};
	margin-bottom: 2px;
`;

const Subtitle = styled.div`
	font-size: 14px;
	color: ${tokens.text.secondary};
`;

const Footer = styled.div`
	margin-top: 6px;
	margin-bottom: -8px;
	font-size: 11px;
	color: ${rgba(tokens.text.secondary, 0.6)};
	text-align: center;
`;

const DismissAction = styled.span`
	cursor: pointer;
	text-decoration: underline;

	&:hover {
		color: ${tokens.text.secondary};
	}
`;

export default function RecommendCard() {
	const { t } = useTranslation();
	const { isWorkspaceSuperuser } = useViewerRoles();
	const enabled = useFeature(FeatureName.ShowRecommendPage);
	const [hidden, setHidden] = useState(() => localStorage.getItem(STORAGE_KEY) !== null);

	if (hidden || !isWorkspaceSuperuser || !enabled) return null;

	const handleDismiss = (event: MouseEvent<HTMLSpanElement>) => {
		event.preventDefault();
		event.stopPropagation();
		localStorage.setItem(STORAGE_KEY, new Date().toISOString().slice(0, 10));
		setHidden(true);
	};

	return (
		<Box>
			<CardLink to="/recommend">
				<BackgroundDecoration src={RecommendBackground} alt="" aria-hidden="true" />
				<Content>
					<Eyebrow>{t('sidebar.recommend.eyebrow')}</Eyebrow>
					<Title>{t('sidebar.recommend.title')}</Title>
					<Subtitle>{t('sidebar.recommend.subtitle')}</Subtitle>
				</Content>
			</CardLink>
			<Footer>
				{t('sidebar.recommend.only-superusers')}{' '}
				<DismissAction onClick={handleDismiss}>{t('common.close')}</DismissAction>
			</Footer>
		</Box>
	);
}
